import { v4 as uuidv4 } from 'uuid'; // for generating a unique ID for each file
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  doc,
  updateDoc,
  setDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  getDoc,
  onSnapshot,
  arrayUnion,
  runTransaction,
} from 'firebase/firestore';
import {
  getAuth,
  signInAnonymously,
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  getRedirectResult,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  linkWithPopup,
  linkWithRedirect,
  linkWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import {
  getNextDailyAvailableAtMillis,
  getResolvedHomeStateFromProfile,
  toMillis as toHomeStateMillis,
} from './homeState.js';

const DEFAULT_BACKEND_BASE_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:8000'
    : 'https://fluffball-api-vhjqcyahqa-uc.a.run.app';
const BACKEND_BASE_URL = (process.env.REACT_APP_BACKEND_URL || DEFAULT_BACKEND_BASE_URL).replace(/\/$/, '');

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIRE_BASE_API_KEY || process.env.FIRE_BASE_API_KEY,
  authDomain: "fastab-f08e9.firebaseapp.com",
  projectId: "fastab-f08e9",
  storageBucket: "fastab-f08e9.appspot.com",
  messagingSenderId: "439667307825",
  appId: "1:439667307825:web:1e0a4cda7ac3bda5376c10",
  measurementId: "G-31TL7C8J18"
};

const app = initializeApp(firebaseConfig);

const storage = getStorage();
const db = getFirestore(app);
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });
const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');
let initialAuthResolutionStarted = false;
let initialAuthResolved = false;
let initialResolvedAuthUser = auth.currentUser && !auth.currentUser.isAnonymous ? auth.currentUser : null;
let initialAuthPromise = null;
let pendingAuthRedirectResolutionPromise = null;
let inMemoryAnalyticsAnonymousId = '';
let inMemoryMergedAnalyticsIdentity = null;

const ensureInitialAuthResolutionListener = () => {
  if (initialAuthResolutionStarted) {
    return;
  }

  initialAuthResolutionStarted = true;
  initialAuthPromise = new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      initialResolvedAuthUser = user && !user.isAnonymous ? user : null;
      initialAuthResolved = true;
      unsubscribe();
      resolve(initialResolvedAuthUser);
    });
  });
};

const ensureFirebaseApiKey = () => {
  if (!firebaseConfig.apiKey) {
    throw new Error("Missing Firebase API key. Set REACT_APP_FIRE_BASE_API_KEY for local development.");
  }
};

const getCurrentUser = () => auth.currentUser;

const hasResolvedInitialAuthState = () => initialAuthResolved;

const waitForInitialAuthState = () => {
  if (initialAuthResolved) {
    return Promise.resolve(initialResolvedAuthUser);
  }

  ensureInitialAuthResolutionListener();
  return initialAuthPromise;
};

const onAuthUserChanged = (callback) => onAuthStateChanged(auth, (user) => {
  initialResolvedAuthUser = user && !user.isAnonymous ? user : null;
  initialAuthResolved = true;
  if (user && !user.isAnonymous) {
    cacheSignedInUser(user);
  } else {
    clearUiBootstrapSession();
  }

  callback(user);
});

const startPendingSocialAuthRedirect = async ({
  providerId,
  flow = 'generic',
  docId = '',
  petType = '',
  redirectClaimContext = null,
}) => {
  const provider = getSocialAuthProvider(providerId);
  const pendingClaimContext = redirectClaimContext || await captureAnonymousClaimContext();
  const didPersistState = writePendingAuthRedirectState({
    flow,
    providerId,
    docId,
    petType,
    previousAnonymousUid: pendingClaimContext.previousAnonymousUid,
    previousAnonymousIdToken: pendingClaimContext.previousAnonymousIdToken,
  });

  if (!didPersistState) {
    throw new Error('This browser blocked sign-in. Open this page in Safari or continue with email instead.');
  }

  try {
    if (auth.currentUser?.isAnonymous) {
      await linkWithRedirect(auth.currentUser, provider);
      return null;
    }

    await signInWithRedirect(auth, provider);
    return null;
  } catch (error) {
    clearPendingAuthRedirectState();
    throw error;
  }
};

const authenticateWithSocialProvider = async ({
  providerId,
  flow = 'generic',
  docId = '',
  petType = '',
  redirectClaimContext = null,
}) => {
  ensureFirebaseApiKey();
  const provider = getSocialAuthProvider(providerId);
  const performPopupAuth = async () => {
    if (auth.currentUser?.isAnonymous) {
      try {
        return await linkWithPopup(auth.currentUser, provider);
      } catch (error) {
        if (!isCredentialConflictError(error)) {
          throw error;
        }

        return resolveSocialConflictCredential(providerId, error);
      }
    }

    return signInWithPopup(auth, provider);
  };

  if (shouldPreferRedirectSocialAuth()) {
    return startPendingSocialAuthRedirect({
      providerId,
      flow,
      docId,
      petType,
      redirectClaimContext,
    });
  }

  try {
    return await performPopupAuth();
  } catch (error) {
    if (!shouldFallbackToRedirectSocialAuth(error)) {
      throw error;
    }

    return startPendingSocialAuthRedirect({
      providerId,
      flow,
      docId,
      petType,
      redirectClaimContext,
    });
  }
};

const signInWithGoogle = async ({ redirectFlow = 'generic' } = {}) => {
  const userCredential = await authenticateWithSocialProvider({
    providerId: 'google',
    flow: redirectFlow,
  });
  if (!userCredential?.user) {
    return null;
  }
  cacheSignedInUser(userCredential.user);
  try {
    await ensureUserProfileDocument(userCredential.user, { authProvider: 'google' });
  } catch (profileError) {
    // Keep auth sign-in successful even if Firestore profile write is blocked.
    // Downstream UI should still show the user as signed in.
    console.error('Failed to ensure user profile after sign-in:', profileError);
  }
  return userCredential.user;
};

const signInWithApple = async ({ redirectFlow = 'generic' } = {}) => {
  const userCredential = await authenticateWithSocialProvider({
    providerId: 'apple',
    flow: redirectFlow,
  });
  if (!userCredential?.user) {
    return null;
  }
  cacheSignedInUser(userCredential.user);
  try {
    await ensureUserProfileDocument(userCredential.user, { authProvider: 'apple' });
  } catch (profileError) {
    console.error('Failed to ensure user profile after Apple sign-in:', profileError);
  }
  return userCredential.user;
};

const signOutCurrentUser = async () => {
  await signOut(auth);
  resetAnalyticsIdentity();
  clearUiBootstrapSession();
};

const getAuthProviderForUser = (user) => {
  if (!user) {
    return 'unknown';
  }

  if (user.isAnonymous) {
    return 'anonymous';
  }

  const providerIds = Array.isArray(user.providerData)
    ? user.providerData
      .map((provider) => String(provider?.providerId || '').trim())
      .filter(Boolean)
    : [];

  if (providerIds.includes('password')) {
    return 'password';
  }
  if (providerIds.includes('google.com')) {
    return 'google';
  }
  if (providerIds.includes('apple.com')) {
    return 'apple';
  }

  return 'unknown';
};

const requireAuthenticatedUser = async ({
  allowAnonymous = false,
  createAnonymousIfMissing = false,
} = {}) => {
  ensureFirebaseApiKey();

  let currentUser = auth.currentUser;
  if (!currentUser && createAnonymousIfMissing) {
    const anonymousCredential = await signInAnonymously(auth);
    currentUser = anonymousCredential.user;
  }

  if (!currentUser || (!allowAnonymous && currentUser.isAnonymous)) {
    throw new Error(allowAnonymous ? 'User is not authenticated.' : 'User is not signed in. Please sign in to continue.');
  }

  await currentUser.getIdToken();
  return currentUser;
};

const ensureFlowUser = async () => requireAuthenticatedUser({
  allowAnonymous: true,
  createAnonymousIfMissing: true,
});

const requireSignedInUser = async () => {
  return requireAuthenticatedUser({
    allowAnonymous: false,
    createAnonymousIfMissing: false,
  });
};

const getAuthenticatedJsonHeaders = async (options = {}) => {
  const user = await requireAuthenticatedUser({
    allowAnonymous: Boolean(options?.allowAnonymous),
    createAnonymousIfMissing: Boolean(options?.createAnonymousIfMissing),
  });
  const idToken = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  };
};

const normalizeEmail = (value) => (value || '').trim().toLowerCase();
const normalizePetName = (value) =>
  (value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const normalizePetType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'cat' ? 'cat' : normalized === 'dog' ? 'dog' : '';
};
const normalizePetQuestionValue = (value) => String(value || '').trim();

const mergeIndexedUrlValues = (existingValues, index, nextValue, targetLength = 3) => {
  const merged = Array.isArray(existingValues) ? [...existingValues] : [];
  while (merged.length < targetLength) {
    merged.push('');
  }
  if (Number.isInteger(index) && index >= 0 && index < targetLength) {
    merged[index] = typeof nextValue === 'string' ? nextValue.trim() : '';
  }
  return merged.slice(0, targetLength);
};

const buildOwnerPayload = (user) => {
  const normalizedEmail = normalizeEmail(user?.email);
  const payload = {
    owner_uid: String(user?.uid || '').trim() || null,
    owner_email: normalizedEmail || null,
    email: normalizedEmail || '',
    owner_is_anonymous: Boolean(user?.isAnonymous),
  };

  if (user && !user.isAnonymous && user.uid) {
    payload.owner_user_doc_path = `${USERS_COLLECTION}/${user.uid}`;
  }

  return payload;
};

const getClaimConflictCode = (error) => String(error?.code || '').trim().toLowerCase();

const getFirestoreWriteErrorCode = (error) => String(error?.code || '').trim().toLowerCase();

const isCredentialConflictError = (error) => {
  const code = getClaimConflictCode(error);
  return (
    code === 'auth/credential-already-in-use'
    || code === 'auth/email-already-in-use'
    || code === 'auth/account-exists-with-different-credential'
  );
};

const captureAnonymousClaimContext = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser || !currentUser.isAnonymous) {
    return { previousAnonymousUid: '', previousAnonymousIdToken: '' };
  }

  return {
    previousAnonymousUid: String(currentUser.uid || '').trim(),
    previousAnonymousIdToken: await currentUser.getIdToken(true),
  };
};

const getPreferredExistingSignInMethod = async (email = '') => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    return '';
  }

  try {
    const signInMethods = await fetchSignInMethodsForEmail(auth, normalizedEmail);
    return signInMethods.find((method) => ['apple.com', 'google.com', 'password'].includes(method)) || '';
  } catch (error) {
    console.warn('Could not look up existing sign-in methods:', error);
    return '';
  }
};

const buildExistingEmailAuthError = async (email = '') => {
  const normalizedEmail = normalizeEmail(email);
  const signInMethod = await getPreferredExistingSignInMethod(normalizedEmail);
  let message = normalizedEmail
    ? 'That email already has an account. Sign in instead.'
    : 'That account already exists. Sign in instead.';

  if (signInMethod === 'password') {
    message = 'That email already has an account. Sign in with email instead.';
  } else if (signInMethod === 'google.com') {
    message = 'That email already has an account. Sign in with Google instead.';
  } else if (signInMethod === 'apple.com') {
    message = 'That email already has an account. Sign in with Apple instead.';
  }

  const authError = new Error(message);
  authError.code = 'auth/account-exists-with-different-credential';
  return authError;
};

const resolveGoogleConflictCredential = async (error) => {
  const code = getClaimConflictCode(error);
  const googleCredential = GoogleAuthProvider.credentialFromError(error);

  if (code === 'auth/account-exists-with-different-credential') {
    throw await buildExistingEmailAuthError(error?.customData?.email);
  }

  if (googleCredential) {
    return signInWithCredential(auth, googleCredential);
  }

  throw error;
};

const resolveAppleConflictCredential = async (error) => {
  const code = getClaimConflictCode(error);
  const appleCredential = OAuthProvider.credentialFromError(error);

  if (code === 'auth/account-exists-with-different-credential') {
    throw await buildExistingEmailAuthError(error?.customData?.email);
  }

  if (appleCredential) {
    return signInWithCredential(auth, appleCredential);
  }

  throw error;
};

const getSocialAuthProvider = (providerId = '') => {
  const normalizedProviderId = String(providerId || '').trim().toLowerCase();

  if (normalizedProviderId === 'google') {
    return googleProvider;
  }
  if (normalizedProviderId === 'apple') {
    return appleProvider;
  }

  throw new Error(`Unsupported auth provider: ${providerId}`);
};

const getSocialAuthProviderLabel = (providerId = '') => {
  const normalizedProviderId = String(providerId || '').trim().toLowerCase();
  return normalizedProviderId === 'apple' ? 'apple' : 'google';
};

const resolveSocialConflictCredential = async (providerId, error) => {
  return getSocialAuthProviderLabel(providerId) === 'apple'
    ? resolveAppleConflictCredential(error)
    : resolveGoogleConflictCredential(error);
};

const isRetryableDraftWriteError = (error) => {
  const code = getFirestoreWriteErrorCode(error);
  return code === 'aborted' || code === 'failed-precondition' || code === 'unavailable';
};

const waitForRetryDelay = (attemptNumber) => new Promise((resolve) => {
  window.setTimeout(resolve, 120 * attemptNumber);
});

const USERS_COLLECTION = 'fluffballusers';
const MAX_INPUT_PHOTO_SETS = 25;
const UI_BOOTSTRAP_CACHE_KEY = 'fluffball_ui_bootstrap_v1';
const UI_BOOTSTRAP_SCHEMA_VERSION = 2;
const HOME_STATE_DEBUG_SESSION_KEY = 'fluffball_debug_home_state';
const ANALYTICS_ANON_ID_KEY = 'fluffball_analytics_anon_id';
const ANALYTICS_MERGED_IDENTITY_KEY = 'fluffball_analytics_merged_identity';
const PENDING_AUTH_REDIRECT_STORAGE_KEY = 'fluffball_pending_auth_redirect_v1';
const PENDING_AUTH_REDIRECT_SCHEMA_VERSION = 1;
const PENDING_AUTH_REDIRECT_MAX_AGE_MS = 30 * 60 * 1000;
const PENDING_EMAIL_LINK_STORAGE_KEY = 'fluffball_pending_email_link_v1';
const PENDING_EMAIL_LINK_SCHEMA_VERSION = 1;
const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due'];
const HOME_STATES = new Set([
  'guest',
  'signed_in_default',
  'subscription_active_no_results',
  'daily_available',
  'daily_generating',
  'daily_ready',
]);

const getLocalStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch (storageError) {
    console.warn('Local storage is unavailable:', storageError);
    return null;
  }
};

const getSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch (storageError) {
    console.warn('Session storage is unavailable:', storageError);
    return null;
  }
};

const getPendingAuthRedirectStorage = () => getSessionStorage() || getLocalStorage();

const clearPendingAuthRedirectState = () => {
  const storageAdapter = getPendingAuthRedirectStorage();
  if (!storageAdapter) {
    return;
  }

  try {
    storageAdapter.removeItem(PENDING_AUTH_REDIRECT_STORAGE_KEY);
  } catch (storageError) {
    console.warn('Failed to clear pending auth redirect state:', storageError);
  }
};

const readPendingAuthRedirectState = () => {
  const storageAdapter = getPendingAuthRedirectStorage();
  if (!storageAdapter) {
    return null;
  }

  try {
    const rawValue = storageAdapter.getItem(PENDING_AUTH_REDIRECT_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    if (!parsedValue || Number(parsedValue.version) !== PENDING_AUTH_REDIRECT_SCHEMA_VERSION) {
      clearPendingAuthRedirectState();
      return null;
    }

    const startedAt = Number(parsedValue.startedAt);
    if (!Number.isFinite(startedAt) || (Date.now() - startedAt) > PENDING_AUTH_REDIRECT_MAX_AGE_MS) {
      clearPendingAuthRedirectState();
      return null;
    }

    const providerId = String(parsedValue.providerId || '').trim().toLowerCase();
    const flow = String(parsedValue.flow || '').trim();
    if (!flow || !['google', 'apple'].includes(providerId)) {
      clearPendingAuthRedirectState();
      return null;
    }

    return {
      startedAt,
      flow,
      providerId,
      docId: String(parsedValue.docId || '').trim(),
      petType: normalizePetType(parsedValue.petType),
      previousAnonymousUid: String(parsedValue.previousAnonymousUid || '').trim(),
      previousAnonymousIdToken: String(parsedValue.previousAnonymousIdToken || '').trim(),
    };
  } catch (storageError) {
    console.warn('Failed to read pending auth redirect state:', storageError);
    clearPendingAuthRedirectState();
    return null;
  }
};

const writePendingAuthRedirectState = (state = {}) => {
  const storageAdapter = getPendingAuthRedirectStorage();
  if (!storageAdapter) {
    return false;
  }

  const nextState = {
    version: PENDING_AUTH_REDIRECT_SCHEMA_VERSION,
    startedAt: Date.now(),
    flow: String(state.flow || '').trim(),
    providerId: String(state.providerId || '').trim().toLowerCase(),
    docId: String(state.docId || '').trim(),
    petType: normalizePetType(state.petType),
    previousAnonymousUid: String(state.previousAnonymousUid || '').trim(),
    previousAnonymousIdToken: String(state.previousAnonymousIdToken || '').trim(),
  };

  if (!nextState.flow || !['google', 'apple'].includes(nextState.providerId)) {
    return false;
  }

  try {
    storageAdapter.setItem(PENDING_AUTH_REDIRECT_STORAGE_KEY, JSON.stringify(nextState));
    return true;
  } catch (storageError) {
    console.warn('Failed to write pending auth redirect state:', storageError);
    clearPendingAuthRedirectState();
    return false;
  }
};

const getPendingEmailLinkStorage = () => getLocalStorage() || getSessionStorage();

const clearPendingEmailLinkState = () => {
  const storageAdapter = getPendingEmailLinkStorage();
  if (!storageAdapter) {
    return;
  }

  try {
    storageAdapter.removeItem(PENDING_EMAIL_LINK_STORAGE_KEY);
  } catch (storageError) {
    console.warn('Failed to clear pending email link state:', storageError);
  }
};

const readPendingEmailLinkState = () => {
  const storageAdapter = getPendingEmailLinkStorage();
  if (!storageAdapter) {
    return null;
  }

  try {
    const rawValue = storageAdapter.getItem(PENDING_EMAIL_LINK_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    if (!parsedValue || Number(parsedValue.version) !== PENDING_EMAIL_LINK_SCHEMA_VERSION) {
      clearPendingEmailLinkState();
      return null;
    }

    return {
      email: normalizeEmail(parsedValue.email),
      flow: String(parsedValue.flow || '').trim(),
      docId: String(parsedValue.docId || '').trim(),
      petType: normalizePetType(parsedValue.petType),
      claimToken: String(parsedValue.claimToken || '').trim(),
    };
  } catch (storageError) {
    console.warn('Failed to read pending email link state:', storageError);
    clearPendingEmailLinkState();
    return null;
  }
};

const writePendingEmailLinkState = (state = {}) => {
  const storageAdapter = getPendingEmailLinkStorage();
  if (!storageAdapter) {
    return false;
  }

  const nextState = {
    version: PENDING_EMAIL_LINK_SCHEMA_VERSION,
    email: normalizeEmail(state.email),
    flow: String(state.flow || '').trim(),
    docId: String(state.docId || '').trim(),
    petType: normalizePetType(state.petType),
    claimToken: String(state.claimToken || '').trim(),
  };

  if (!nextState.email) {
    return false;
  }

  try {
    storageAdapter.setItem(PENDING_EMAIL_LINK_STORAGE_KEY, JSON.stringify(nextState));
    return true;
  } catch (storageError) {
    console.warn('Failed to write pending email link state:', storageError);
    clearPendingEmailLinkState();
    return false;
  }
};

const extractEmailLinkContextFromSearchParams = (searchParams) => {
  if (!searchParams) {
    return {
      flow: '',
      docId: '',
      petType: '',
      claimToken: '',
    };
  }

  return {
    flow: String(searchParams.get(EMAIL_LINK_FLOW_PARAM) || '').trim(),
    docId: String(searchParams.get(EMAIL_LINK_DOC_ID_PARAM) || '').trim(),
    petType: normalizePetType(searchParams.get(EMAIL_LINK_PET_TYPE_PARAM)),
    claimToken: String(searchParams.get(EMAIL_LINK_CLAIM_TOKEN_PARAM) || '').trim(),
  };
};

const getEmailLinkContextFromUrl = (urlValue = '') => {
  if (typeof window === 'undefined') {
    return {
      flow: '',
      docId: '',
      petType: '',
      claimToken: '',
    };
  }

  try {
    const url = new URL(urlValue || window.location.href, window.location.origin);
    const directContext = extractEmailLinkContextFromSearchParams(url.searchParams);
    if (directContext.flow || directContext.docId || directContext.claimToken) {
      return directContext;
    }

    const continueUrlValue = String(url.searchParams.get('continueUrl') || '').trim();
    if (!continueUrlValue) {
      return directContext;
    }

    try {
      const continueUrl = new URL(continueUrlValue, window.location.origin);
      return extractEmailLinkContextFromSearchParams(continueUrl.searchParams);
    } catch (parseError) {
      console.warn('Failed to parse email-link continueUrl:', parseError);
      return directContext;
    }
  } catch (parseError) {
    console.warn('Failed to parse email-link URL:', parseError);
    return {
      flow: '',
      docId: '',
      petType: '',
      claimToken: '',
    };
  }
};

const getPendingEmailLinkContext = () => {
  const storedState = readPendingEmailLinkState();
  const urlContext = getEmailLinkContextFromUrl();
  return {
    email: storedState?.email || '',
    flow: urlContext.flow || storedState?.flow || '',
    docId: urlContext.docId || storedState?.docId || '',
    petType: urlContext.petType || storedState?.petType || '',
    claimToken: urlContext.claimToken || storedState?.claimToken || '',
  };
};

const buildEmailLinkContinueUrl = ({
  flow = '',
  docId = '',
  petType = '',
  claimToken = '',
} = {}) => {
  if (typeof window === 'undefined') {
    return '';
  }

  const url = new URL(window.location.href);
  ['apiKey', 'mode', 'oobCode', 'lang', 'continueUrl'].forEach((param) => {
    url.searchParams.delete(param);
  });

  const nextValues = {
    [EMAIL_LINK_FLOW_PARAM]: String(flow || '').trim(),
    [EMAIL_LINK_DOC_ID_PARAM]: String(docId || '').trim(),
    [EMAIL_LINK_PET_TYPE_PARAM]: normalizePetType(petType),
    [EMAIL_LINK_CLAIM_TOKEN_PARAM]: String(claimToken || '').trim(),
  };

  Object.entries(nextValues).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
      return;
    }
    url.searchParams.delete(key);
  });

  return url.toString();
};

const clearEmailLinkParamsFromUrl = () => {
  if (typeof window === 'undefined' || typeof window.history?.replaceState !== 'function') {
    return;
  }

  const url = new URL(window.location.href);
  [
    'apiKey',
    'mode',
    'oobCode',
    'lang',
    'continueUrl',
    EMAIL_LINK_FLOW_PARAM,
    EMAIL_LINK_DOC_ID_PARAM,
    EMAIL_LINK_PET_TYPE_PARAM,
    EMAIL_LINK_CLAIM_TOKEN_PARAM,
  ].forEach((param) => {
    url.searchParams.delete(param);
  });

  const nextRelativeUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, document.title, nextRelativeUrl);
};

const isPopupRestrictedInAppBrowser = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = String(navigator.userAgent || navigator.vendor || '').trim();
  return POPUP_RESTRICTED_IN_APP_BROWSER_REGEX.test(userAgent);
};

const shouldPreferRedirectSocialAuth = () => {
  return isPopupRestrictedInAppBrowser();
};

const shouldFallbackToRedirectSocialAuth = (error) => {
  const code = getClaimConflictCode(error);
  return code === 'auth/popup-blocked';
};

const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MOBILE_WEB_USER_AGENT_REGEX = /Android.+Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|webOS|Windows Phone/i;
const TABLET_WEB_USER_AGENT_REGEX = /iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i;
const POPUP_RESTRICTED_IN_APP_BROWSER_REGEX = /Instagram|FBAN|FBAV|FB_IAB|FBIOS/i;
const EMAIL_LINK_FLOW_PARAM = 'fluffballEmailFlow';
const EMAIL_LINK_DOC_ID_PARAM = 'fluffballEmailDocId';
const EMAIL_LINK_PET_TYPE_PARAM = 'fluffballEmailPetType';
const EMAIL_LINK_CLAIM_TOKEN_PARAM = 'fluffballEmailClaimToken';

const createAnalyticsAnonymousId = () => uuidv4().toLowerCase();

const normalizeAnalyticsAnonymousId = (value) => {
  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) {
    return '';
  }

  if (UUID_V4_REGEX.test(trimmedValue)) {
    return trimmedValue.toLowerCase();
  }

  const legacyMatch = trimmedValue.match(/^anon-([0-9a-f-]{36})$/i);
  if (legacyMatch && UUID_V4_REGEX.test(legacyMatch[1])) {
    return legacyMatch[1].toLowerCase();
  }

  return '';
};

const getOrCreateAnalyticsAnonymousId = () => {
  const storageAdapter = getLocalStorage();
  const persistAnonymousId = (anonymousId) => {
    inMemoryAnalyticsAnonymousId = anonymousId;
    if (storageAdapter) {
      storageAdapter.setItem(ANALYTICS_ANON_ID_KEY, anonymousId);
    }
    return anonymousId;
  };

  try {
    const cachedId = normalizeAnalyticsAnonymousId(
      storageAdapter ? storageAdapter.getItem(ANALYTICS_ANON_ID_KEY) : inMemoryAnalyticsAnonymousId
    );
    if (cachedId) {
      return persistAnonymousId(cachedId);
    }

    return persistAnonymousId(createAnalyticsAnonymousId());
  } catch (storageError) {
    console.warn('Failed to access analytics anonymous id storage:', storageError);
    if (inMemoryAnalyticsAnonymousId) {
      return inMemoryAnalyticsAnonymousId;
    }
    inMemoryAnalyticsAnonymousId = createAnalyticsAnonymousId();
    return inMemoryAnalyticsAnonymousId;
  }
};

const getAnalyticsAuthenticatedUserId = (userLike = null) => {
  const explicitUid = String(userLike?.uid || '').trim();
  if (explicitUid) {
    return explicitUid;
  }

  const currentUid = String(auth.currentUser?.uid || '').trim();
  if (currentUid && !auth.currentUser?.isAnonymous) {
    return currentUid;
  }

  return '';
};

const getAnalyticsEventContext = (userLike = null) => {
  const anonymousId = getOrCreateAnalyticsAnonymousId();
  const authenticatedUserId = getAnalyticsAuthenticatedUserId(userLike);
  return {
    anonymousId,
    authenticatedUserId,
    userId: authenticatedUserId || anonymousId,
  };
};

const getAnalyticsSurface = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'desktop_web';
  }

  const userAgent = String(navigator.userAgent || navigator.vendor || '').trim();
  const userAgentDataMobile = navigator.userAgentData?.mobile === true;
  const isIpadOsDesktopMode = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const hasCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches === true;
  const hasNarrowViewport = window.matchMedia?.('(max-width: 767px)').matches === true;

  if (userAgentDataMobile || MOBILE_WEB_USER_AGENT_REGEX.test(userAgent)) {
    return 'mobile_web';
  }

  if (isIpadOsDesktopMode || TABLET_WEB_USER_AGENT_REGEX.test(userAgent)) {
    return 'tablet_web';
  }

  if (hasCoarsePointer && hasNarrowViewport) {
    return 'mobile_web';
  }

  if (hasCoarsePointer) {
    return 'tablet_web';
  }

  return 'desktop_web';
};

const shouldPreferMagicLinkEmailAuth = () => {
  return isPopupRestrictedInAppBrowser();
};

const readMergedAnalyticsIdentity = () => {
  const storageAdapter = getLocalStorage();

  try {
    const rawValue = storageAdapter
      ? storageAdapter.getItem(ANALYTICS_MERGED_IDENTITY_KEY)
      : inMemoryMergedAnalyticsIdentity
        ? JSON.stringify(inMemoryMergedAnalyticsIdentity)
        : '';
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    const anonymousId = normalizeAnalyticsAnonymousId(parsedValue?.anonymousId);
    const authenticatedUserId = String(parsedValue?.authenticatedUserId || '').trim();
    if (!anonymousId || !authenticatedUserId) {
      return null;
    }

    const normalizedValue = { anonymousId, authenticatedUserId };
    inMemoryMergedAnalyticsIdentity = normalizedValue;
    return normalizedValue;
  } catch (storageError) {
    console.warn('Failed to read analytics merged identity state:', storageError);
    return inMemoryMergedAnalyticsIdentity;
  }
};

const writeMergedAnalyticsIdentity = (value = null) => {
  const storageAdapter = getLocalStorage();
  inMemoryMergedAnalyticsIdentity = value;

  if (!storageAdapter) {
    return;
  }

  try {
    if (!value) {
      storageAdapter.removeItem(ANALYTICS_MERGED_IDENTITY_KEY);
      return;
    }

    storageAdapter.setItem(ANALYTICS_MERGED_IDENTITY_KEY, JSON.stringify(value));
  } catch (storageError) {
    console.warn('Failed to write analytics merged identity state:', storageError);
  }
};

const hasMergedAnalyticsIdentity = (anonymousId, authenticatedUserId) => {
  const mergedIdentity = readMergedAnalyticsIdentity();
  return Boolean(
    mergedIdentity &&
    mergedIdentity.anonymousId === normalizeAnalyticsAnonymousId(anonymousId) &&
    mergedIdentity.authenticatedUserId === String(authenticatedUserId || '').trim()
  );
};

const markAnalyticsIdentityMerged = (anonymousId, authenticatedUserId) => {
  const normalizedAnonymousId = normalizeAnalyticsAnonymousId(anonymousId);
  const normalizedAuthenticatedUserId = String(authenticatedUserId || '').trim();
  if (!normalizedAnonymousId || !normalizedAuthenticatedUserId) {
    return;
  }

  writeMergedAnalyticsIdentity({
    anonymousId: normalizedAnonymousId,
    authenticatedUserId: normalizedAuthenticatedUserId,
  });
};

const resetAnalyticsIdentity = () => {
  const storageAdapter = getLocalStorage();
  const nextAnonymousId = createAnalyticsAnonymousId();

  inMemoryAnalyticsAnonymousId = nextAnonymousId;
  if (storageAdapter) {
    try {
      storageAdapter.setItem(ANALYTICS_ANON_ID_KEY, nextAnonymousId);
    } catch (storageError) {
      console.warn('Failed to reset analytics anonymous id storage:', storageError);
    }
  }

  writeMergedAnalyticsIdentity(null);
  return nextAnonymousId;
};

const getAnalyticsUserId = (userLike = null) => {
  return getAnalyticsEventContext(userLike).userId;
};

const isLocalDevRuntime = () => {
  if (process.env.NODE_ENV !== 'production') {
    return true;
  }

  if (typeof window !== 'undefined') {
    const browserHostname = String(window.location?.hostname || '').trim().toLowerCase();
    if (
      browserHostname === 'localhost'
      || browserHostname === '127.0.0.1'
      || browserHostname === '::1'
      || browserHostname.endsWith('.local')
    ) {
      return true;
    }
  }

  try {
    const backendUrl = new URL(BACKEND_BASE_URL);
    const backendHostname = String(backendUrl.hostname || '').trim().toLowerCase();
    return (
      backendHostname === 'localhost'
      || backendHostname === '127.0.0.1'
      || backendHostname === '::1'
      || backendHostname.endsWith('.local')
    );
  } catch (urlError) {
    return false;
  }
};

const logAnalyticsEvent = ({
  userId,
  eventName,
  properties = {},
  analyticsContext = null,
}) => {
  const context = analyticsContext && typeof analyticsContext === 'object'
    ? analyticsContext
    : getAnalyticsEventContext();
  const effectiveAnonymousId = normalizeAnalyticsAnonymousId(context.anonymousId) || getOrCreateAnalyticsAnonymousId();
  const effectiveAuthenticatedUserId = String(context.authenticatedUserId || '').trim();
  const effectiveUserId = String(
    userId ||
    context.userId ||
    effectiveAuthenticatedUserId ||
    effectiveAnonymousId ||
    getAnalyticsUserId()
  ).trim();
  const effectiveEventName = String(eventName || '').trim();
  if (!effectiveUserId || !effectiveEventName) {
    return;
  }

  const shouldMergeIds = Boolean(
    effectiveAnonymousId &&
    effectiveAuthenticatedUserId &&
    !hasMergedAnalyticsIdentity(effectiveAnonymousId, effectiveAuthenticatedUserId)
  );

  const payload = {
    userId: effectiveUserId,
    anonymousId: effectiveAnonymousId,
    authenticatedUserId: effectiveAuthenticatedUserId || undefined,
    shouldMergeIds,
    eventName: effectiveEventName,
    properties: {
      ...(properties && typeof properties === 'object' ? properties : {}),
      app: 'fluffball-web',
      platform: 'web',
      surface: getAnalyticsSurface(),
    },
    dev_mode: isLocalDevRuntime(),
  };

  try {
    fetch(`${BACKEND_BASE_URL}/log_event`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }).then(async (response) => {
      if (!response.ok || !shouldMergeIds) {
        return;
      }

      let responseJson = null;
      try {
        responseJson = await response.json();
      } catch (parseError) {
        responseJson = null;
      }

      const identityMerged = typeof responseJson?.identity_merged === 'boolean'
        ? responseJson.identity_merged
        : Boolean(effectiveAnonymousId && effectiveAuthenticatedUserId);
      if (identityMerged) {
        markAnalyticsIdentityMerged(effectiveAnonymousId, effectiveAuthenticatedUserId);
      }
    }).catch((analyticsError) => {
      console.debug('[Analytics] Failed to log event:', effectiveEventName, analyticsError);
    });
  } catch (analyticsError) {
    console.debug('[Analytics] Error logging event:', effectiveEventName, analyticsError);
  }
};

const getHomeStateDebugEnabled = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  let queryFlag = false;
  try {
    queryFlag = new URLSearchParams(window.location.search).get('debugHomeState') === '1';
  } catch (queryError) {
    console.warn('Failed to parse debugHomeState query param:', queryError);
  }

  const sessionStorageAdapter = getSessionStorage();
  if (queryFlag && sessionStorageAdapter) {
    try {
      sessionStorageAdapter.setItem(HOME_STATE_DEBUG_SESSION_KEY, '1');
    } catch (storageError) {
      console.warn('Failed to persist home state debug flag:', storageError);
    }
  }

  if (queryFlag) {
    return true;
  }

  if (!sessionStorageAdapter) {
    return false;
  }

  try {
    return sessionStorageAdapter.getItem(HOME_STATE_DEBUG_SESSION_KEY) === '1';
  } catch (storageError) {
    console.warn('Failed to read home state debug flag:', storageError);
    return false;
  }
};

const getHomeStateDebugHistory = () => {
  if (typeof window === 'undefined' || !Array.isArray(window.__fluffballHomeStateDebug)) {
    return [];
  }
  return window.__fluffballHomeStateDebug;
};

const pushHomeStateDebugEvent = (label, payload = {}) => {
  if (!getHomeStateDebugEnabled()) {
    return null;
  }

  const entry = {
    at: new Date().toISOString(),
    label,
    payload,
  };

  try {
    if (typeof window !== 'undefined') {
      const existing = Array.isArray(window.__fluffballHomeStateDebug)
        ? window.__fluffballHomeStateDebug
        : [];
      window.__fluffballHomeStateDebug = [...existing, entry].slice(-50);
      window.dispatchEvent(new CustomEvent('fluffball:home-state-debug', { detail: entry }));
    }
  } catch (debugWriteError) {
    console.warn('Failed to write home state debug history:', debugWriteError);
  }

  console.log('[Fluffball Home Debug]', entry);
  return entry;
};

const readUiBootstrapCache = () => {
  const storageAdapter = getLocalStorage();
  if (!storageAdapter) {
    return { last_user: null, users: {} };
  }

  try {
    const rawValue = storageAdapter.getItem(UI_BOOTSTRAP_CACHE_KEY);
    if (!rawValue) {
      return { last_user: null, users: {} };
    }

    const parsed = JSON.parse(rawValue);
    return {
      last_user: parsed?.last_user && typeof parsed.last_user === 'object' ? parsed.last_user : null,
      users: parsed?.users && typeof parsed.users === 'object' ? parsed.users : {},
    };
  } catch (parseError) {
    console.warn('Failed to read Fluffball UI bootstrap cache:', parseError);
    return { last_user: null, users: {} };
  }
};

const isCurrentBootstrapEntry = (value) => (
  Boolean(
    value &&
    typeof value === 'object' &&
    Number(value.cache_schema_version) === UI_BOOTSTRAP_SCHEMA_VERSION
  )
);

const writeUiBootstrapCache = (updater) => {
  const storageAdapter = getLocalStorage();
  const currentValue = readUiBootstrapCache();
  const nextValue = updater({
    ...currentValue,
    users: { ...currentValue.users },
  }) || currentValue;

  if (!storageAdapter) {
    return nextValue;
  }

  try {
    storageAdapter.setItem(UI_BOOTSTRAP_CACHE_KEY, JSON.stringify(nextValue));
  } catch (writeError) {
    console.warn('Failed to write Fluffball UI bootstrap cache:', writeError);
  }

  return nextValue;
};

const normalizeResultDocIds = (value) => (
  Array.isArray(value)
    ? value
      .filter((item) => typeof item === 'string' && item.trim())
      .map((item) => item.trim())
    : []
);

const normalizeCachedUrls = (value) => (
  Array.isArray(value)
    ? value
      .filter((item) => typeof item === 'string' && item.trim())
      .map((item) => item.trim())
    : []
);

const toCachedMillis = (value) => {
  return toHomeStateMillis(value);
};

const isSubscriptionActiveFromProfile = (profileData) => {
  const subscriptionStatus = String(profileData?.subscription_status || '').trim().toLowerCase();
  return Boolean(
    profileData?.subscription_active ||
    ACTIVE_SUBSCRIPTION_STATUSES.includes(subscriptionStatus)
  );
};

const canAccessProfileFromProfile = (profileData) => {
  if (!profileData || typeof profileData !== 'object') {
    return false;
  }

  const latestInputPhotoUrls = normalizeCachedUrls(profileData.latest_input_photo_urls);
  if (latestInputPhotoUrls.length >= 5) {
    return true;
  }

  if (
    Array.isArray(profileData.input_photo_sets) &&
    profileData.input_photo_sets.some((entry) => normalizeCachedUrls(entry?.photo_urls).length >= 5)
  ) {
    return true;
  }

  if (String(profileData.latest_result_doc_id || '').trim()) {
    return true;
  }
  if (String(profileData.latest_primary_result_doc_id || '').trim()) {
    return true;
  }
  if (String(profileData.daily_last_doc_id || '').trim()) {
    return true;
  }
  if (String(profileData.daily_active_doc_id || '').trim()) {
    return true;
  }
  if (String(profileData.last_unlocked_doc_id || '').trim()) {
    return true;
  }
  return normalizeResultDocIds(profileData.result_doc_ids).length > 0;
};

const hasAuthoritativeHomeProfileData = (profileData) => {
  if (!profileData || typeof profileData !== 'object') {
    return false;
  }

  if (String(profileData.home_state || '').trim()) {
    return true;
  }
  if (String(profileData.daily_generation_status || '').trim()) {
    return true;
  }
  if (String(profileData.daily_last_completed_date || '').trim()) {
    return true;
  }
  if (String(profileData.daily_last_doc_id || '').trim()) {
    return true;
  }
  if (String(profileData.daily_active_doc_id || '').trim()) {
    return true;
  }
  if (Boolean(profileData.has_completed_first_set)) {
    return true;
  }
  if (String(profileData.latest_primary_result_doc_id || '').trim()) {
    return true;
  }
  if (normalizeCachedUrls(profileData.latest_input_photo_urls).length > 0) {
    return true;
  }
  if (typeof profileData.subscription_active === 'boolean') {
    return true;
  }
  return Boolean(String(profileData.subscription_status || '').trim());
};

const getHomeStateFromProfile = (userLike, profileData = null, fallbackHeroState = '') => {
  if (!userLike) {
    return 'guest';
  }

  const subscriptionActive = isSubscriptionActiveFromProfile(profileData);
  const latestPrimaryResultDocId = String(profileData?.latest_primary_result_doc_id || '').trim();
  const latestInputPhotoUrls = normalizeCachedUrls(profileData?.latest_input_photo_urls);
  const resultDocIds = normalizeResultDocIds(profileData?.result_doc_ids);
  const hasCompletedFirstSet = Boolean(
    profileData?.has_completed_first_set ||
    latestPrimaryResultDocId ||
    (latestInputPhotoUrls.length >= 5 && resultDocIds.length > 0)
  );
  const homeState = getResolvedHomeStateFromProfile({
    userLike,
    profileData,
    fallbackHeroState,
    subscriptionActive,
    hasCompletedFirstSet,
  });

  if (HOME_STATES.has(homeState)) {
    return homeState;
  }
  return 'daily_available';
};

const getHomeActionDocIdFromProfile = (profileData = null, fallbackDocId = '') => {
  const homeState = getHomeStateFromProfile({ uid: 'cached-user' }, profileData);
  const latestResultDocId = String(profileData?.latest_result_doc_id || fallbackDocId || '').trim();
  const latestPrimaryResultDocId = String(profileData?.latest_primary_result_doc_id || '').trim();
  const dailyActiveDocId = String(profileData?.daily_active_doc_id || '').trim();
  const dailyLastDocId = String(profileData?.daily_last_doc_id || '').trim();

  if (homeState === 'daily_generating') {
    return dailyActiveDocId || latestResultDocId;
  }
  if (homeState === 'daily_ready') {
    return dailyLastDocId || latestResultDocId;
  }
  if (latestPrimaryResultDocId) {
    return latestPrimaryResultDocId;
  }
  return latestResultDocId;
};

const buildHomepageSnapshotFromProfile = (userLike, profileData = null, fallbackHomepage = null) => {
  if (!userLike) {
    return {
      heroState: 'guest',
      hasCompletedResult: false,
      latestResultDocId: '',
      homeActionDocId: '',
      subscriptionActive: false,
      canCreateDailyFromHome: false,
    };
  }

  const fallbackState = fallbackHomepage && typeof fallbackHomepage === 'object' ? fallbackHomepage : {};
  const latestResultDocId = String(
    profileData?.latest_result_doc_id ||
    fallbackState.latestResultDocId ||
    ''
  ).trim();
  const latestPrimaryResultDocId = String(profileData?.latest_primary_result_doc_id || '').trim();
  const latestInputPhotoUrls = normalizeCachedUrls(profileData?.latest_input_photo_urls);
  const resultDocIds = normalizeResultDocIds(profileData?.result_doc_ids);
  const hasCompletedFirstSet = Boolean(
    profileData?.has_completed_first_set ||
    latestPrimaryResultDocId ||
    (latestInputPhotoUrls.length >= 5 && resultDocIds.length > 0)
  );
  const homeState = getHomeStateFromProfile(userLike, profileData, fallbackState.heroState || '');
  const subscriptionActive = isSubscriptionActiveFromProfile(profileData);
  const homeActionDocId = getHomeActionDocIdFromProfile(profileData, latestResultDocId);
  const hasCompletedResult = Boolean(
    hasCompletedFirstSet ||
    fallbackState.hasCompletedResult
  );

  return {
    heroState: homeState,
    hasCompletedResult,
    latestResultDocId,
    homeActionDocId,
    subscriptionActive,
    canCreateDailyFromHome: homeState === 'daily_available',
    dailyNextAvailableAtMs: getNextDailyAvailableAtMillis(profileData),
  };
};

const cacheHomepageSnapshotFromProfile = (userLike, profileData = null) => {
  cacheHomepageSnapshot(
    userLike,
    buildHomepageSnapshotFromProfile(userLike, profileData)
  );
};

const getUserHint = (userLike) => {
  if (!userLike || typeof userLike !== 'object') {
    return null;
  }

  const uid = typeof userLike.uid === 'string' ? userLike.uid.trim() : '';
  if (!uid) {
    return null;
  }

  return {
    uid,
    email: normalizeEmail(userLike.email),
    displayName: typeof userLike.displayName === 'string' ? userLike.displayName : '',
  };
};

const withCachedUserBucket = (userLike, updateBucket) => {
  const userHint = getUserHint(userLike);
  if (!userHint) {
    return null;
  }

  writeUiBootstrapCache((currentValue) => {
    const existingBucket = currentValue.users[userHint.uid] || {};
    const nextBucket = { ...existingBucket };
    updateBucket(nextBucket, userHint);
    return {
      ...currentValue,
      last_user: userHint,
      users: {
        ...currentValue.users,
        [userHint.uid]: nextBucket,
      },
    };
  });

  return userHint;
};

const cacheSignedInUser = (userLike) => {
  const userHint = withCachedUserBucket(userLike, () => {});
  pushHomeStateDebugEvent('cache_signed_in_user', {
    uid: userHint?.uid || '',
    email: userHint?.email || '',
  });
};

const clearUiBootstrapSession = () => {
  writeUiBootstrapCache((currentValue) => ({
    ...currentValue,
    last_user: null,
  }));
  pushHomeStateDebugEvent('clear_ui_bootstrap_session');
};

const cacheUserProfileSnapshot = (userLike, profileData = null) => {
  withCachedUserBucket(userLike, (bucket, userHint) => {
    const profile = profileData && typeof profileData === 'object' ? profileData : {};
    bucket.profile = {
      cache_schema_version: UI_BOOTSTRAP_SCHEMA_VERSION,
      uid: userHint.uid,
      email: normalizeEmail(profile.email || userHint.email),
      subscription_active: Boolean(profile.subscription_active),
      subscription_status: String(profile.subscription_status || ''),
      subscription_cancel_at_period_end: Boolean(profile.subscription_cancel_at_period_end),
      stripe_customer_id: String(profile.stripe_customer_id || ''),
      home_state: String(profile.home_state || ''),
      home_action_doc_id: String(profile.home_action_doc_id || ''),
      has_completed_first_set: Boolean(profile.has_completed_first_set),
      latest_result_doc_id: String(profile.latest_result_doc_id || ''),
      latest_primary_result_doc_id: String(profile.latest_primary_result_doc_id || ''),
      latest_pet_type: String(profile.latest_pet_type || ''),
      latest_pet_name: String(profile.latest_pet_name || ''),
      latest_input_photo_urls: normalizeCachedUrls(profile.latest_input_photo_urls),
      daily_generation_status: String(profile.daily_generation_status || ''),
      daily_active_doc_id: String(profile.daily_active_doc_id || ''),
      daily_last_doc_id: String(profile.daily_last_doc_id || ''),
      daily_last_completed_date: String(profile.daily_last_completed_date || ''),
      daily_next_available_at_ms: toCachedMillis(profile.daily_next_available_at),
      result_doc_ids: normalizeResultDocIds(profile.result_doc_ids),
      updated_at_ms: Date.now(),
    };
    pushHomeStateDebugEvent('cache_user_profile_snapshot', {
      uid: userHint.uid,
      profile: bucket.profile,
    });
  });
};

const cacheHomepageSnapshot = (userLike, homepageState = {}) => {
  withCachedUserBucket(userLike, (bucket, userHint) => {
    const previousHomepage =
      isCurrentBootstrapEntry(bucket.homepage) ? bucket.homepage : {};
    const nextHomepage = {
      ...previousHomepage,
      cache_schema_version: UI_BOOTSTRAP_SCHEMA_VERSION,
      updated_at_ms: Date.now(),
    };

    if (Object.prototype.hasOwnProperty.call(homepageState, 'hasCompletedResult')) {
      nextHomepage.hasCompletedResult = Boolean(homepageState.hasCompletedResult);
    }

    if (Object.prototype.hasOwnProperty.call(homepageState, 'latestResultDocId')) {
      nextHomepage.latestResultDocId = String(homepageState.latestResultDocId || '').trim();
    }

    if (Object.prototype.hasOwnProperty.call(homepageState, 'subscriptionActive')) {
      nextHomepage.subscriptionActive = Boolean(homepageState.subscriptionActive);
    }

    if (Object.prototype.hasOwnProperty.call(homepageState, 'canCreateDailyFromHome')) {
      nextHomepage.canCreateDailyFromHome = Boolean(homepageState.canCreateDailyFromHome);
    }

    if (typeof homepageState.heroState === 'string' && homepageState.heroState.trim()) {
      nextHomepage.heroState = homepageState.heroState.trim();
    }

    if (Object.prototype.hasOwnProperty.call(homepageState, 'homeActionDocId')) {
      nextHomepage.homeActionDocId = String(homepageState.homeActionDocId || '').trim();
    }

    if (Object.prototype.hasOwnProperty.call(homepageState, 'dailyNextAvailableAtMs')) {
      nextHomepage.dailyNextAvailableAtMs = Number(homepageState.dailyNextAvailableAtMs) || 0;
    }

    bucket.homepage = nextHomepage;
    pushHomeStateDebugEvent('cache_homepage_snapshot', {
      uid: userHint.uid,
      homepage: nextHomepage,
    });
  });
};

const cacheProfileDocumentsSnapshot = (userLike, documents = []) => {
  withCachedUserBucket(userLike, (bucket) => {
    bucket.documents = Array.isArray(documents)
      ? documents
        .filter((docData) => docData && typeof docData.id === 'string' && docData.id.trim())
        .map((docData) => ({
          id: docData.id.trim(),
          generation_status: String(docData.generation_status || ''),
          output_videos: Array.isArray(docData.output_videos)
            ? docData.output_videos.filter((url) => typeof url === 'string' && url.trim())
            : [],
          preview_video_url: String(docData.preview_video_url || ''),
          unlock_source: String(docData.unlock_source || ''),
          subscription_access_active: typeof docData.subscription_access_active === 'boolean'
            ? docData.subscription_access_active
            : null,
          has_paid: Boolean(docData.has_paid),
          payment_status: String(docData.payment_status || ''),
          is_unlocked: Boolean(docData.is_unlocked),
          purchase_unlocked: Boolean(docData.purchase_unlocked),
          timestamp: toCachedMillis(docData.timestamp),
        }))
      : [];
  });
};

const getUiBootstrapSnapshot = () => {
  const liveUser = auth.currentUser && !auth.currentUser.isAnonymous ? auth.currentUser : null;
  const cache = readUiBootstrapCache();
  const cachedUser = cache.last_user && typeof cache.last_user === 'object' ? cache.last_user : null;
  const authUser = liveUser || cachedUser || null;
  const userBucket = authUser?.uid ? cache.users?.[authUser.uid] || {} : {};
  const cachedProfile = isCurrentBootstrapEntry(userBucket.profile) ? userBucket.profile : null;
  const cachedHomepage = isCurrentBootstrapEntry(userBucket.homepage) ? userBucket.homepage : null;

  const snapshot = {
    authUser,
    userProfile: cachedProfile,
    hasCachedProfile: Boolean(cachedProfile),
    homepage: cachedHomepage,
    hasCachedHomepage: Boolean(cachedHomepage),
    documents: Array.isArray(userBucket.documents) ? userBucket.documents : [],
    hasCachedDocuments: Array.isArray(userBucket.documents),
  };

  pushHomeStateDebugEvent('read_ui_bootstrap_snapshot', {
    liveUserUid: liveUser?.uid || '',
    cachedUserUid: cachedUser?.uid || '',
    cachedProfileAccepted: Boolean(cachedProfile),
    cachedHomepageAccepted: Boolean(cachedHomepage),
    rawCachedProfileVersion: Number(userBucket?.profile?.cache_schema_version || 0),
    rawCachedHomepageVersion: Number(userBucket?.homepage?.cache_schema_version || 0),
    snapshot,
  });

  return snapshot;
};

const getUserDocRef = (uid) => doc(db, USERS_COLLECTION, uid);

const ensureUserProfileDocument = async (userArg = null, options = {}) => {
  const user = userArg || (await requireSignedInUser());
  if (user.isAnonymous) {
    throw new Error('Anonymous users do not have a persisted profile document.');
  }
  await user.getIdToken();
  const userRef = getUserDocRef(user.uid);
  const normalizedEmail = normalizeEmail(user.email);

  const payload = {
    uid: user.uid,
    email: normalizedEmail || null,
    email_normalized: normalizedEmail || null,
    auth_provider: String(options?.authProvider || getAuthProviderForUser(user) || 'unknown'),
    last_login_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };

  await setDoc(userRef, payload, { merge: true });
  return userRef;
};

const getUserProfile = async (uid) => {
  if (!uid) {
    return null;
  }
  const profileSnap = await getDoc(getUserDocRef(uid));
  if (!profileSnap.exists()) {
    return null;
  }
  return { id: profileSnap.id, ...profileSnap.data() };
};

const subscribeToUserProfile = (uid, callback) => {
  if (!uid) {
    callback(null);
    return () => {};
  }
  return onSnapshot(getUserDocRef(uid), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
};

const subscribeToPetPhotoDocument = (docId, callback) => {
  const trimmedDocId = String(docId || '').trim();
  if (!trimmedDocId) {
    callback(null);
    return () => {};
  }

  return onSnapshot(doc(db, 'petPhotos', trimmedDocId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
};

// Function to save email to Firebase
async function saveEmailToFirebase(email) {
  try {
    const docRef = doc(collection(db, "emails"));
    await setDoc(docRef, {
      email: email,
      timestamp: serverTimestamp() // This is how you call serverTimestamp in modular SDK
    });
    console.log("Document written with ID: ", docRef.id);
  } catch (e) {
    console.error("Error adding document: ", e);
  }
}

async function logCheckoutClick() {
  try {
    // Add a new document with a generated id.
    const docRef = doc(collection(db, "holiday_checkout_clicks"));
    await setDoc(docRef, {
      timestamp: serverTimestamp(), // Capture the exact time of click
      // You can add more fields here if you need to capture additional data
    });
    console.log("Checkout click logged with ID: ", docRef.id);
  } catch (e) {
    console.error("Error logging checkout click: ", e);
  }
}

const getFileExtension = (fileName) => {
  if (!fileName || typeof fileName !== 'string') {
    return 'jpg';
  }

  const trimmedName = fileName.trim();
  const lastDotIndex = trimmedName.lastIndexOf('.');
  if (lastDotIndex < 0 || lastDotIndex === trimmedName.length - 1) {
    return 'jpg';
  }

  return trimmedName.slice(lastDotIndex + 1).toLowerCase();
};


const validateReferencePhotoUrl = async ({ imageUrl, petType, photoIndex = 0, uploadId = '' }) => {
  const endpoint = `${BACKEND_BASE_URL}/validate_reference_photo_url`;
  const headers = await getAuthenticatedJsonHeaders({ allowAnonymous: true, createAnonymousIfMissing: true });
  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      image_url: imageUrl,
      pet_type: petType,
      photo_index: photoIndex,
      upload_id: uploadId,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Failed to validate photo (${response.status})`;
    let errorCode = '';
    try {
      const payload = await response.json();
      errorMessage = payload?.error || errorMessage;
      errorCode = String(payload?.code || '').trim();
    } catch (parseError) {
      const responseText = await response.text();
      errorMessage = responseText || errorMessage;
    }
    const validationError = new Error(errorMessage);
    validationError.code = errorCode;
    throw validationError;
  }

  return response.json();
};

const prepareReferencePhotoUrl = async ({ imageUrl, petType, photoIndex = 0, uploadId = '' }) => {
  const endpoint = `${BACKEND_BASE_URL}/prepare_reference_photo_url`;
  const headers = await getAuthenticatedJsonHeaders({ allowAnonymous: true, createAnonymousIfMissing: true });
  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      image_url: imageUrl,
      pet_type: petType,
      photo_index: photoIndex,
      upload_id: uploadId,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Failed to prepare photo (${response.status})`;
    try {
      const payload = await response.json();
      errorMessage = payload?.error || errorMessage;
    } catch (parseError) {
      const responseText = await response.text();
      errorMessage = responseText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

const validateSelectedReferencePhotoFile = async ({ file, petType, photoIndex = 0 }) => {
  const authUser = await ensureFlowUser();
  const normalizedPetType = normalizePetType(petType);
  if (!normalizedPetType) {
    throw new Error('Please choose cat or dog before uploading photos.');
  }

  const tempUploadId = uuidv4();
  const fileExtension = getFileExtension(file?.name);
  const tempRef = ref(
    storage,
    `christmasCards/${authUser.uid}/_validation/${tempUploadId}.${fileExtension}`
  );
  const uploadTask = uploadBytesResumable(tempRef, file, {
    contentType: file?.type || 'image/jpeg',
  });

  await new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      () => {},
      (error) => reject(error),
      () => resolve()
    );
  });

  const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
  await validateReferencePhotoUrl({
    imageUrl: downloadURL,
    petType: normalizedPetType,
    photoIndex,
    uploadId: tempUploadId,
  });
  return {
    uploadId: tempUploadId,
    validationUrl: downloadURL,
  };
};

const createDraftPetPhotoDoc = async ({ petType, petName = '' } = {}) => {
  const authUser = await ensureFlowUser();
  const userId = uuidv4();
  const userDocRef = doc(db, 'petPhotos', userId);
  const normalizedPetType = normalizePetType(petType);
  const cleanedPetName = normalizePetName(petName);
  const ownerPayload = buildOwnerPayload(authUser);

  await setDoc(userDocRef, {
    timestamp: serverTimestamp(),
    generation_status: 'draft',
    generation_progress: 0,
    has_paid: false,
    ...ownerPayload,
    petType: normalizedPetType || null,
    pet_name: cleanedPetName || null,
    photos: {
      YourPet: [],
    },
    reference_photo_validation_status: 'pending',
  });

  return userId;
};

const persistPreparedReferencePhoto = async ({
  docId,
  photoIndex = 0,
  validationUrl = '',
  processedReferencePhotoUrl = '',
  petType = '',
}) => {
  const trimmedDocId = String(docId || '').trim();
  if (!trimmedDocId) {
    return;
  }

  const authUser = await ensureFlowUser();
  const normalizedPetType = normalizePetType(petType);
  const userDocRef = doc(db, 'petPhotos', trimmedDocId);
  const ownerPayload = buildOwnerPayload(authUser);

  for (let attempt = 1; attempt <= 3; attempt += 1) {
    try {
      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(userDocRef);
        if (!snapshot.exists()) {
          return;
        }

        const existingData = snapshot.data() || {};
        const existingPhotos = existingData?.photos && typeof existingData.photos === 'object'
          ? existingData.photos
          : {};
        const currentPetPhotos = Array.isArray(existingPhotos.YourPet) ? existingPhotos.YourPet : [];
        const currentProcessedRefs = Array.isArray(existingData.processed_reference_photo_urls)
          ? existingData.processed_reference_photo_urls
          : [];

        const nextPetPhotos = mergeIndexedUrlValues(currentPetPhotos, photoIndex, validationUrl);
        const nextProcessedRefs = mergeIndexedUrlValues(currentProcessedRefs, photoIndex, processedReferencePhotoUrl);
        const preparedReferenceCount = nextProcessedRefs.filter((url) => typeof url === 'string' && url.trim()).length;

        transaction.set(
          userDocRef,
          {
            ...ownerPayload,
            petType: normalizedPetType || existingData.petType || null,
            photos: {
              ...existingPhotos,
              YourPet: nextPetPhotos,
            },
            processed_reference_photo_urls: nextProcessedRefs,
            reference_photo_validation_status: preparedReferenceCount === 3 ? 'validated' : 'approved',
            reference_photo_validation_updated_at: serverTimestamp(),
            processed_reference_updated_at: serverTimestamp(),
          },
          { merge: true }
        );
      });
      return;
    } catch (error) {
      if (!isRetryableDraftWriteError(error) || attempt >= 3) {
        throw error;
      }
      await waitForRetryDelay(attempt);
    }
  }
};

const onUploadPhotos = async (familyMembers, photos, options = {}) => {
  const authUser = await ensureFlowUser();
  const isAnonymousFlowUser = Boolean(authUser?.isAnonymous);
  const userProfileRef = isAnonymousFlowUser ? null : await ensureUserProfileDocument(authUser);
  const storageUserId = authUser.uid;
  const existingDocId = String(options?.docId || '').trim();
  const userId = existingDocId || uuidv4();
  const userDocRef = doc(db, "petPhotos", userId);
  const normalizedPetType = normalizePetType(options?.petType);
  const cleanedPetName = normalizePetName(options?.petName || '');
  const ownerPayload = buildOwnerPayload(authUser);
  console.log("Uploading photos: ", photos)

  try {
    const userPhotosMap = {};

    for (const member of familyMembers) {
      const memberPhotos = photos[member.name]?.files;
      const memberValidationUrls = Array.isArray(photos[member.name]?.validationUrls)
        ? photos[member.name].validationUrls
          .filter((url) => typeof url === 'string' && url.trim())
        : [];
      if (!memberPhotos || memberPhotos.length === 0) continue;

      if (memberValidationUrls.length === memberPhotos.length) {
        userPhotosMap[member.name] = memberValidationUrls;
      } else {
        userPhotosMap[member.name] = [];

        for (const photo of memberPhotos) {
          const photoId = uuidv4();
          const fileExtension = getFileExtension(photo.name);
          const photoRef = ref(storage, `christmasCards/${storageUserId}/${userId}/${member.name}/${photoId}.${fileExtension}`);
          const uploadTask = uploadBytesResumable(photoRef, photo, {
            contentType: photo.type || 'image/jpeg',
          });

          await new Promise((resolve, reject) => {
            uploadTask.on('state_changed', 
              () => {},
              (error) => {
                console.error('Upload failed: ', error);
                reject(error);
              }, 
              async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                console.log(downloadURL)
                userPhotosMap[member.name].push(downloadURL);
                resolve();
              }
            );
          });
        }
      }
    }

    // Save the user's photos map to Firestore
    const processedReferencePhotoUrls = Array.isArray(photos?.YourPet?.processedValidationUrls)
      ? photos.YourPet.processedValidationUrls.slice(0, 3).map((url) => (typeof url === 'string' ? url.trim() : ''))
      : [];

    let mergedProcessedReferencePhotoUrls = processedReferencePhotoUrls;
    if (existingDocId) {
      try {
        const existingSnapshot = await getDoc(userDocRef);
        const existingData = existingSnapshot.data() || {};
        const existingProcessedRefs = Array.isArray(existingData.processed_reference_photo_urls)
          ? existingData.processed_reference_photo_urls.slice(0, 3).map((url) => (typeof url === 'string' ? url.trim() : ''))
          : [];
        mergedProcessedReferencePhotoUrls = [0, 1, 2].map((index) => (
          processedReferencePhotoUrls[index]
          || existingProcessedRefs[index]
          || ''
        ));
      } catch (readError) {
        console.warn('Could not read existing draft result doc before upload completion.', readError);
      }
    }

    const preparedReferenceCount = mergedProcessedReferencePhotoUrls.filter((url) => url).length;

    await setDoc(userDocRef, {
      photos: userPhotosMap,
      timestamp: serverTimestamp(),
      generation_status: 'uploaded',
      generation_progress: 0,
      has_paid: false,
      ...ownerPayload,
      petType: normalizedPetType || null,
      pet_name: cleanedPetName || null,
      reference_photo_validation_status: preparedReferenceCount === 3 ? 'validated' : 'approved',
      reference_photo_validation_updated_at: serverTimestamp(),
      ...(mergedProcessedReferencePhotoUrls.length > 0
        ? {
            processed_reference_photo_urls: mergedProcessedReferencePhotoUrls,
            processed_reference_updated_at: serverTimestamp(),
          }
        : {}),
    }, { merge: true });

    const referencePhotoUrls = Array.isArray(userPhotosMap?.YourPet)
      ? userPhotosMap.YourPet.filter((url) => typeof url === 'string' && url.trim())
      : [];

    if (userProfileRef) {
      let existingPhotoSets = [];
      try {
        const userProfileSnapshot = await getDoc(userProfileRef);
        existingPhotoSets = Array.isArray(userProfileSnapshot.data()?.input_photo_sets)
          ? userProfileSnapshot.data().input_photo_sets
          : [];
      } catch (readError) {
        if (readError?.code !== 'permission-denied') {
          throw readError;
        }
        console.warn('Profile read denied while appending input photo sets; writing without history merge.');
        existingPhotoSets = [];
      }

      const ownerEmail = normalizeEmail(authUser.email);
      const nextPhotoSets = [
        {
          doc_id: userId,
          photo_urls: referencePhotoUrls,
          created_at_iso: new Date().toISOString(),
        },
        ...existingPhotoSets.filter((entry) => entry?.doc_id !== userId),
      ].slice(0, MAX_INPUT_PHOTO_SETS);

      await setDoc(
        userProfileRef,
        {
          uid: storageUserId,
          email: ownerEmail || null,
          email_normalized: ownerEmail || null,
          latest_result_doc_id: userId,
          latest_input_photo_urls: referencePhotoUrls,
          result_doc_ids: arrayUnion(userId),
          input_photo_sets: nextPhotoSets,
          updated_at: serverTimestamp(),
        },
        { merge: true }
      );
    }

    console.log("All photos uploaded successfully.");
    return userId; // Returning the UUID of the user's document
  } catch (error) {
    console.error("Error uploading photos: ", error);
    throw error; // Rethrowing the error for further handling
  }
};

const updateEmailAndPetTypeInFirebase = async (userId, email, petType, utmParams) => {
  const authUser = await requireSignedInUser();
  const userProfileRef = await ensureUserProfileDocument(authUser);
  const userDocRef = doc(db, "petPhotos", userId);
  const normalizedEmail = normalizeEmail(email) || normalizeEmail(authUser.email);
  try {
    await updateDoc(userDocRef, {
      email: normalizedEmail,
      owner_uid: authUser.uid,
      owner_email: normalizeEmail(authUser.email) || normalizedEmail || null,
      owner_user_doc_path: `${USERS_COLLECTION}/${authUser.uid}`,
      owner_is_anonymous: false,
      petType: petType,
      utmParams: utmParams,
    });

    await setDoc(
      userProfileRef,
      {
        uid: authUser.uid,
        email: normalizeEmail(authUser.email) || normalizedEmail || null,
        email_normalized: normalizeEmail(authUser.email) || normalizedEmail || null,
        latest_result_doc_id: userId,
        latest_pet_type: petType || null,
        result_doc_ids: arrayUnion(userId),
        updated_at: serverTimestamp(),
      },
      { merge: true }
    );
    console.log(userId)
    console.log("Email updated successfully");
  } catch (error) {
    console.error("Error updating email: ", error);
    throw error;
  }
};

const createResultClaimTicket = async ({ docId }) => {
  const trimmedDocId = String(docId || '').trim();
  if (!trimmedDocId) {
    return '';
  }

  const endpoint = `${BACKEND_BASE_URL}/create_result_claim_ticket`;
  const headers = await getAuthenticatedJsonHeaders({
    allowAnonymous: true,
    createAnonymousIfMissing: true,
  });
  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      doc_id: trimmedDocId,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Failed to prepare your sign-in link (${response.status})`;
    try {
      const payload = await response.json();
      errorMessage = payload?.error || errorMessage;
    } catch (parseError) {
      const responseText = await response.text();
      errorMessage = responseText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  try {
    const payload = await response.json();
    return String(payload?.claim_token || '').trim();
  } catch (parseError) {
    console.warn('Could not parse claim ticket response:', parseError);
    return '';
  }
};

const claimResultDocForSignedInUser = async ({
  docId,
  previousIdToken = '',
  claimToken = '',
}) => {
  const trimmedDocId = String(docId || '').trim();
  const trimmedPreviousIdToken = String(previousIdToken || '').trim();
  const trimmedClaimToken = String(claimToken || '').trim();
  if (!trimmedDocId || (!trimmedPreviousIdToken && !trimmedClaimToken)) {
    return null;
  }

  const endpoint = `${BACKEND_BASE_URL}/claim_result_doc`;
  const headers = await getAuthenticatedJsonHeaders();
  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      doc_id: trimmedDocId,
      previous_id_token: trimmedPreviousIdToken,
      claim_token: trimmedClaimToken,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Failed to claim your draft (${response.status})`;
    try {
      const payload = await response.json();
      errorMessage = payload?.error || errorMessage;
    } catch (parseError) {
      const responseText = await response.text();
      errorMessage = responseText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

const finalizeFlowAuthSuccess = async ({
  user,
  docId,
  previousAnonymousUid = '',
  previousAnonymousIdToken = '',
  claimToken = '',
  authProvider = '',
}) => {
  const resolvedUser = user || auth.currentUser;
  const trimmedDocId = String(docId || '').trim();
  const trimmedClaimToken = String(claimToken || '').trim();
  if (!resolvedUser || resolvedUser.isAnonymous) {
    throw new Error('Sign in did not complete. Please try again.');
  }

  if (trimmedDocId) {
    const shouldClaimWithPreviousToken = (
      previousAnonymousUid
      && previousAnonymousIdToken
      && String(resolvedUser.uid || '').trim()
      && String(resolvedUser.uid || '').trim() !== previousAnonymousUid
    );

    if (shouldClaimWithPreviousToken) {
      await claimResultDocForSignedInUser({
        docId: trimmedDocId,
        previousIdToken: previousAnonymousIdToken,
      });
    } else if (trimmedClaimToken) {
      await claimResultDocForSignedInUser({
        docId: trimmedDocId,
        claimToken: trimmedClaimToken,
      });
    }
  }

  cacheSignedInUser(resolvedUser);
  await ensureUserProfileDocument(resolvedUser, {
    authProvider: authProvider || getAuthProviderForUser(resolvedUser),
  });
  return resolvedUser;
};

const sendEmailSignInLinkForFlow = async ({
  email,
  flow = 'generic',
  docId = '',
  petType = '',
} = {}) => {
  ensureFirebaseApiKey();

  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    throw new Error('Please enter your email address.');
  }

  const trimmedDocId = String(docId || '').trim();
  const normalizedPetType = normalizePetType(petType);
  const claimToken = trimmedDocId
    ? await createResultClaimTicket({ docId: trimmedDocId })
    : '';
  const continueUrl = buildEmailLinkContinueUrl({
    flow,
    docId: trimmedDocId,
    petType: normalizedPetType,
    claimToken,
  });

  await sendSignInLinkToEmail(auth, normalizedEmail, {
    url: continueUrl,
    handleCodeInApp: true,
  });

  writePendingEmailLinkState({
    email: normalizedEmail,
    flow,
    docId: trimmedDocId,
    petType: normalizedPetType,
    claimToken,
  });

  return {
    email: normalizedEmail,
    flow,
    docId: trimmedDocId,
    petType: normalizedPetType,
  };
};

const completePendingEmailLinkSignIn = async ({ email } = {}) => {
  ensureFirebaseApiKey();

  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    throw new Error('Please enter the email address you used for the sign-in link.');
  }
  if (typeof window === 'undefined' || !isSignInWithEmailLink(auth, window.location.href)) {
    throw new Error('This sign-in link is invalid or has already been used.');
  }

  const pendingContext = getPendingEmailLinkContext();

  try {
    const userCredential = await signInWithEmailLink(auth, normalizedEmail, window.location.href);
    const resolvedUser = await finalizeFlowAuthSuccess({
      user: userCredential?.user,
      docId: pendingContext.docId,
      claimToken: pendingContext.claimToken,
      authProvider: 'email_link',
    });

    clearPendingEmailLinkState();
    clearEmailLinkParamsFromUrl();

    return {
      status: 'success',
      flow: pendingContext.flow,
      docId: pendingContext.docId,
      petType: pendingContext.petType,
      user: resolvedUser,
    };
  } catch (error) {
    return {
      status: 'error',
      flow: pendingContext.flow,
      docId: pendingContext.docId,
      petType: pendingContext.petType,
      error,
    };
  }
};

const consumePendingEmailLinkSignIn = async () => {
  if (typeof window === 'undefined' || !isSignInWithEmailLink(auth, window.location.href)) {
    return null;
  }

  const pendingContext = getPendingEmailLinkContext();
  if (!pendingContext.email) {
    return {
      status: 'awaiting_email',
      flow: pendingContext.flow,
      docId: pendingContext.docId,
      petType: pendingContext.petType,
      claimToken: pendingContext.claimToken,
    };
  }

  return completePendingEmailLinkSignIn({
    email: pendingContext.email,
  });
};

const consumePendingAuthRedirectResult = async () => {
  const pendingState = readPendingAuthRedirectState();
  if (!pendingState) {
    return null;
  }

  if (pendingAuthRedirectResolutionPromise) {
    return pendingAuthRedirectResolutionPromise;
  }

  pendingAuthRedirectResolutionPromise = (async () => {
    try {
      let userCredential = null;

      try {
        userCredential = await getRedirectResult(auth);
      } catch (error) {
        userCredential = await resolveSocialConflictCredential(pendingState.providerId, error);
      }

      if (!userCredential?.user) {
        return {
          status: 'cancelled',
          ...pendingState,
        };
      }

      const resolvedUser = await finalizeFlowAuthSuccess({
        user: userCredential.user,
        docId: pendingState.docId,
        previousAnonymousUid: pendingState.previousAnonymousUid,
        previousAnonymousIdToken: pendingState.previousAnonymousIdToken,
        authProvider: getSocialAuthProviderLabel(pendingState.providerId),
      });

      return {
        status: 'success',
        ...pendingState,
        user: resolvedUser,
      };
    } catch (error) {
      return {
        status: 'error',
        ...pendingState,
        error,
      };
    } finally {
      clearPendingAuthRedirectState();
      pendingAuthRedirectResolutionPromise = null;
    }
  })();

  return pendingAuthRedirectResolutionPromise;
};

const completeFlowSignInWithGoogle = async ({
  docId,
  redirectFlow = 'generic',
  petType = '',
} = {}) => {
  const { previousAnonymousUid, previousAnonymousIdToken } = await captureAnonymousClaimContext();
  const userCredential = await authenticateWithSocialProvider({
    providerId: 'google',
    flow: redirectFlow,
    docId,
    petType,
    redirectClaimContext: {
      previousAnonymousUid,
      previousAnonymousIdToken,
    },
  });

  if (!userCredential?.user) {
    return null;
  }

  return finalizeFlowAuthSuccess({
    user: userCredential?.user,
    docId,
    previousAnonymousUid,
    previousAnonymousIdToken,
    authProvider: 'google',
  });
};

const completeFlowSignInWithApple = async ({
  docId,
  redirectFlow = 'generic',
  petType = '',
} = {}) => {
  const { previousAnonymousUid, previousAnonymousIdToken } = await captureAnonymousClaimContext();
  const userCredential = await authenticateWithSocialProvider({
    providerId: 'apple',
    flow: redirectFlow,
    docId,
    petType,
    redirectClaimContext: {
      previousAnonymousUid,
      previousAnonymousIdToken,
    },
  });

  if (!userCredential?.user) {
    return null;
  }

  return finalizeFlowAuthSuccess({
    user: userCredential?.user,
    docId,
    previousAnonymousUid,
    previousAnonymousIdToken,
    authProvider: 'apple',
  });
};

const completeFlowEmailPasswordAuth = async ({
  docId,
  email,
  password,
  mode = 'create',
}) => {
  ensureFirebaseApiKey();

  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    throw new Error('Please enter your email address.');
  }
  if (!password) {
    throw new Error('Please enter a password.');
  }

  const { previousAnonymousUid, previousAnonymousIdToken } = await captureAnonymousClaimContext();
  let userCredential = null;

  if (mode === 'create') {
    if (auth.currentUser?.isAnonymous) {
      try {
        userCredential = await linkWithCredential(
          auth.currentUser,
          EmailAuthProvider.credential(normalizedEmail, password),
        );
      } catch (error) {
        if (isCredentialConflictError(error)) {
          try {
            userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password);
          } catch (signInError) {
            const existingAccountError = new Error('That email already has an account. Sign in instead.');
            existingAccountError.code = getClaimConflictCode(signInError) || 'auth/email-already-in-use';
            throw existingAccountError;
          }
        } else {
          throw error;
        }
      }
    } else {
      userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
    }
  } else {
    userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password);
  }

  return finalizeFlowAuthSuccess({
    user: userCredential?.user,
    docId,
    previousAnonymousUid,
    previousAnonymousIdToken,
    authProvider: 'password',
  });
};

const updatePetTypeInFirebase = async (userId, petType, utmParams, petName = '') => {
  const authUser = await requireSignedInUser();
  const userProfileRef = await ensureUserProfileDocument(authUser);
  const userDocRef = doc(db, "petPhotos", userId);
  const cleanedPetName = normalizePetName(petName);
  try {
    await updateDoc(userDocRef, {
      petType: petType,
      pet_name: cleanedPetName,
      utmParams: utmParams,
      generation_status: 'queued',
      generation_progress: 5,
      owner_uid: authUser.uid,
      owner_email: normalizeEmail(authUser.email) || null,
      owner_user_doc_path: `${USERS_COLLECTION}/${authUser.uid}`,
      owner_is_anonymous: false,
    });

    await setDoc(
      userProfileRef,
      {
        uid: authUser.uid,
        email: normalizeEmail(authUser.email) || null,
        email_normalized: normalizeEmail(authUser.email) || null,
        latest_result_doc_id: userId,
        latest_pet_type: petType || null,
        latest_pet_name: cleanedPetName || null,
        result_doc_ids: arrayUnion(userId),
        updated_at: serverTimestamp(),
      },
      { merge: true }
    );
    console.log("Pet type updated successfully");
  } catch (error) {
    console.error("Error updating pet type: ", error);
    throw error;
  }
};

const updatePetQuestionnaireInFirebase = async (
  userId,
  updates = {}
) => {
  const {
    petName,
    petPersonality,
    petAgeBucket,
    petDescriptor,
    petFavoriteTrait,
    petFavoriteActivity,
    petSocialStyle,
    petEnergyLevel,
    petHappyPlace,
    petAffectionLevel,
    petConfidenceStyle,
    petVocalStyle,
    petTreatMotivation,
    petPlayStyle,
    petMischiefLevel,
    petRoutineStyle,
  } = updates;
  const authUser = await requireSignedInUser();
  const userProfileRef = await ensureUserProfileDocument(authUser);
  const userDocRef = doc(db, "petPhotos", userId);

  const docUpdates = {
    owner_uid: authUser.uid,
    owner_email: normalizeEmail(authUser.email) || null,
    owner_user_doc_path: `${USERS_COLLECTION}/${authUser.uid}`,
    owner_is_anonymous: false,
  };
  const profileUpdates = {
    uid: authUser.uid,
    email: normalizeEmail(authUser.email) || null,
    email_normalized: normalizeEmail(authUser.email) || null,
    latest_result_doc_id: userId,
    updated_at: serverTimestamp(),
  };

  if (Object.prototype.hasOwnProperty.call(updates, 'petName')) {
    const cleanedPetName = normalizePetName(petName);
    docUpdates.pet_name = cleanedPetName || null;
    profileUpdates.latest_pet_name = cleanedPetName || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petPersonality')) {
    const cleanedPetPersonality = normalizePetQuestionValue(petPersonality);
    docUpdates.pet_personality = cleanedPetPersonality || null;
    profileUpdates.latest_pet_personality = cleanedPetPersonality || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petAgeBucket')) {
    const cleanedPetAgeBucket = normalizePetQuestionValue(petAgeBucket);
    docUpdates.pet_age_bucket = cleanedPetAgeBucket || null;
    profileUpdates.latest_pet_age_bucket = cleanedPetAgeBucket || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petDescriptor')) {
    const cleanedPetDescriptor = normalizePetQuestionValue(petDescriptor);
    docUpdates.pet_descriptor = cleanedPetDescriptor || null;
    profileUpdates.latest_pet_descriptor = cleanedPetDescriptor || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petFavoriteTrait')) {
    const cleanedPetFavoriteTrait = normalizePetQuestionValue(petFavoriteTrait);
    docUpdates.pet_favorite_trait = cleanedPetFavoriteTrait || null;
    profileUpdates.latest_pet_favorite_trait = cleanedPetFavoriteTrait || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petFavoriteActivity')) {
    const cleanedPetFavoriteActivity = normalizePetQuestionValue(petFavoriteActivity);
    docUpdates.pet_favorite_activity = cleanedPetFavoriteActivity || null;
    profileUpdates.latest_pet_favorite_activity = cleanedPetFavoriteActivity || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petSocialStyle')) {
    const cleanedPetSocialStyle = normalizePetQuestionValue(petSocialStyle);
    docUpdates.pet_social_style = cleanedPetSocialStyle || null;
    profileUpdates.latest_pet_social_style = cleanedPetSocialStyle || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petEnergyLevel')) {
    const cleanedPetEnergyLevel = normalizePetQuestionValue(petEnergyLevel);
    docUpdates.pet_energy_level = cleanedPetEnergyLevel || null;
    profileUpdates.latest_pet_energy_level = cleanedPetEnergyLevel || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petHappyPlace')) {
    const cleanedPetHappyPlace = normalizePetQuestionValue(petHappyPlace);
    docUpdates.pet_happy_place = cleanedPetHappyPlace || null;
    profileUpdates.latest_pet_happy_place = cleanedPetHappyPlace || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petAffectionLevel')) {
    const cleanedPetAffectionLevel = normalizePetQuestionValue(petAffectionLevel);
    docUpdates.pet_affection_level = cleanedPetAffectionLevel || null;
    profileUpdates.latest_pet_affection_level = cleanedPetAffectionLevel || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petConfidenceStyle')) {
    const cleanedPetConfidenceStyle = normalizePetQuestionValue(petConfidenceStyle);
    docUpdates.pet_confidence_style = cleanedPetConfidenceStyle || null;
    profileUpdates.latest_pet_confidence_style = cleanedPetConfidenceStyle || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petVocalStyle')) {
    const cleanedPetVocalStyle = normalizePetQuestionValue(petVocalStyle);
    docUpdates.pet_vocal_style = cleanedPetVocalStyle || null;
    profileUpdates.latest_pet_vocal_style = cleanedPetVocalStyle || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petTreatMotivation')) {
    const cleanedPetTreatMotivation = normalizePetQuestionValue(petTreatMotivation);
    docUpdates.pet_treat_motivation = cleanedPetTreatMotivation || null;
    profileUpdates.latest_pet_treat_motivation = cleanedPetTreatMotivation || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petPlayStyle')) {
    const cleanedPetPlayStyle = normalizePetQuestionValue(petPlayStyle);
    docUpdates.pet_play_style = cleanedPetPlayStyle || null;
    profileUpdates.latest_pet_play_style = cleanedPetPlayStyle || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petMischiefLevel')) {
    const cleanedPetMischiefLevel = normalizePetQuestionValue(petMischiefLevel);
    docUpdates.pet_mischief_level = cleanedPetMischiefLevel || null;
    profileUpdates.latest_pet_mischief_level = cleanedPetMischiefLevel || null;
  }

  if (Object.prototype.hasOwnProperty.call(updates, 'petRoutineStyle')) {
    const cleanedPetRoutineStyle = normalizePetQuestionValue(petRoutineStyle);
    docUpdates.pet_routine_style = cleanedPetRoutineStyle || null;
    profileUpdates.latest_pet_routine_style = cleanedPetRoutineStyle || null;
  }

  await updateDoc(userDocRef, docUpdates);
  await setDoc(userProfileRef, profileUpdates, { merge: true });
};

const startGenerationJob = async (docId, useFal = true) => {
  const endpoint = `${BACKEND_BASE_URL}/process_docs`;
  const headers = await getAuthenticatedJsonHeaders();
  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      doc_ids: [docId],
      use_fal: useFal,
    }),
  });

  if (!response.ok) {
    const responseText = await response.text();
    throw new Error(`Failed to start generation job (${response.status}): ${responseText}`);
  }
};

const createSubscriptionCheckoutSession = async ({
  docId,
  userUid,
  customerEmail,
  stripeCustomerId,
  plan,
  successUrl,
  cancelUrl,
}) => {
  const endpoint = `${BACKEND_BASE_URL}/create_subscription_checkout_session`;
  const headers = await getAuthenticatedJsonHeaders();
  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      doc_id: docId,
      user_uid: userUid || '',
      customer_email: customerEmail || '',
      stripe_customer_id: stripeCustomerId || '',
      plan: String(plan || '').trim().toLowerCase(),
      success_url: successUrl,
      cancel_url: cancelUrl,
    }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      `Failed to create subscription checkout session (${response.status}): ${responseData.error || 'Unknown error'}`
    );
  }

  if (!responseData?.url) {
    throw new Error('Stripe checkout URL was not returned by backend.');
  }

  return responseData;
};

// Win-back checkout links are opened from email by mobile-only users with no
// web session, so this endpoint is intentionally unauthenticated: the opaque
// token from the email link is the only credential.
const createWinbackCheckoutSession = async ({ token, successUrl, cancelUrl }) => {
  const endpoint = `${BACKEND_BASE_URL}/winback_checkout_session`;
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token: token || '',
      success_url: successUrl,
      cancel_url: cancelUrl,
    }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(
      `Failed to create win-back checkout session (${response.status}): ${responseData.error || 'Unknown error'}`
    );
    error.status = response.status;
    error.code = responseData.code || '';
    throw error;
  }

  if (!responseData?.url) {
    throw new Error('Stripe checkout URL was not returned by backend.');
  }

  return responseData;
};

// Fetches the pet display payload (name, pronouns, blurred preview media,
// paywall sounds, tier pricing) for the win-back offer page. Unauthenticated:
// the signed token is the credential, same as the checkout session endpoint.
const fetchWinbackOfferDetails = async ({ token }) => {
  const endpoint = `${BACKEND_BASE_URL}/winback_offer_details`;
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: token || '' }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(
      `Failed to load win-back offer details (${response.status}): ${responseData.error || 'Unknown error'}`
    );
    error.status = response.status;
    error.code = responseData.code || '';
    throw error;
  }

  return responseData;
};

// Fetches support metadata for the post-checkout success page. This remains
// available after the user is subscribed, unlike the offer payload endpoint.
const fetchWinbackSupportDetails = async ({ token }) => {
  const endpoint = `${BACKEND_BASE_URL}/winback_support_details`;
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: token || '' }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(
      `Failed to load win-back support details (${response.status}): ${responseData.error || 'Unknown error'}`
    );
    error.status = response.status;
    error.code = responseData.code || '';
    throw error;
  }

  return responseData;
};

const createBillingPortalSession = async ({ stripeCustomerId, returnUrl }) => {
  const endpoint = `${BACKEND_BASE_URL}/create_billing_portal_session`;
  const headers = await getAuthenticatedJsonHeaders();
  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      stripe_customer_id: stripeCustomerId || '',
      return_url: returnUrl,
    }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      `Failed to create billing portal session (${response.status}): ${responseData.error || 'Unknown error'}`
    );
  }

  if (!responseData?.url) {
    throw new Error('Billing portal URL was not returned by backend.');
  }

  return responseData;
};

const openDeferredExternalUrl = async (resolveUrl) => {
  const urlResolver =
    typeof resolveUrl === 'function' ? resolveUrl : async () => resolveUrl;

  let reservedWindow = null;
  if (typeof window !== 'undefined') {
    try {
      reservedWindow = window.open('', '_blank');
      if (reservedWindow) {
        reservedWindow.opener = null;
      }
    } catch (popupError) {
      reservedWindow = null;
    }
  }

  try {
    const resolvedUrl = String(await urlResolver()).trim();
    if (!resolvedUrl) {
      throw new Error('External URL was empty.');
    }

    if (reservedWindow && !reservedWindow.closed) {
      reservedWindow.location.replace(resolvedUrl);
    } else if (typeof window !== 'undefined') {
      window.location.assign(resolvedUrl);
    }

    return resolvedUrl;
  } catch (error) {
    if (reservedWindow && !reservedWindow.closed) {
      reservedWindow.close();
    }
    throw error;
  }
};

const createShareLink = async ({ docId }) => {
  const endpoint = `${BACKEND_BASE_URL}/create_share_link`;
  const headers = await getAuthenticatedJsonHeaders();
  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({ doc_id: docId }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      `Failed to create share link (${response.status}): ${responseData.error || 'Unknown error'}`
    );
  }

  if (!responseData?.share_id || !responseData?.share_url) {
    throw new Error('Share link payload missing share_id/share_url.');
  }
  return responseData;
};

const fetchShareById = async (shareId) => {
  const cleanedShareId = String(shareId || '').trim();
  if (!cleanedShareId) {
    throw new Error('shareId is required');
  }

  const endpoint = `${BACKEND_BASE_URL}/share/${encodeURIComponent(cleanedShareId)}?format=json`;
  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });
  const responseData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      `Failed to load share link (${response.status}): ${responseData.error || 'Unknown error'}`
    );
  }
  return responseData;
};

const startDailyVideoGeneration = async ({ userUid, sourceDocId }) => {
  const endpoint = `${BACKEND_BASE_URL}/generate_daily_video`;
  const headers = await getAuthenticatedJsonHeaders();
  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      user_uid: userUid || '',
      source_doc_id: sourceDocId || '',
    }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      `Failed to start daily generation (${response.status}): ${responseData.error || 'Unknown error'}`
    );
  }

  if (!responseData?.doc_id) {
    throw new Error('Daily generation response missing doc_id.');
  }
  return responseData;
};

const debugResultAccess = async ({ docId }) => {
  const cleanedDocId = String(docId || '').trim();
  if (!cleanedDocId) {
    throw new Error('docId is required');
  }

  const headers = await getAuthenticatedJsonHeaders();
  const endpoint = `${BACKEND_BASE_URL}/debug_result_access?doc_id=${encodeURIComponent(cleanedDocId)}`;
  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      Authorization: headers.Authorization,
    },
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      `Failed to debug result access (${response.status}): ${responseData.error || 'Unknown error'}`
    );
  }

  return responseData;
};

const inferMimeTypeFromFilename = (filename) => {
  const cleanedFilename = String(filename || '').trim().toLowerCase();
  if (cleanedFilename.endsWith('.mp4')) return 'video/mp4';
  if (cleanedFilename.endsWith('.mov')) return 'video/quicktime';
  if (cleanedFilename.endsWith('.webm')) return 'video/webm';
  if (cleanedFilename.endsWith('.jpg') || cleanedFilename.endsWith('.jpeg')) return 'image/jpeg';
  if (cleanedFilename.endsWith('.png')) return 'image/png';
  if (cleanedFilename.endsWith('.webp')) return 'image/webp';
  return 'application/octet-stream';
};

const isTouchDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  return (
    navigator.maxTouchPoints > 0 ||
    window.matchMedia?.('(pointer: coarse)').matches === true
  );
};

const downloadMediaViaBackend = async ({ mediaUrl, filename, preferNativeShare = false }) => {
  const cleanedUrl = String(mediaUrl || '').trim();
  if (!cleanedUrl) {
    throw new Error('mediaUrl is required');
  }

  const endpoint = `${BACKEND_BASE_URL}/download_media?url=${encodeURIComponent(cleanedUrl)}&filename=${encodeURIComponent(
    String(filename || 'download.bin')
  )}`;

  const response = await fetch(endpoint, { method: 'GET' });
  if (!response.ok) {
    const responseText = await response.text().catch(() => '');
    throw new Error(`Failed to download file (${response.status}): ${responseText || 'Unknown error'}`);
  }

  const responseBlob = await response.blob();
  const contentType = response.headers.get('Content-Type') || inferMimeTypeFromFilename(filename);
  const blob = responseBlob.type ? responseBlob : responseBlob.slice(0, responseBlob.size, contentType);

  if (
    preferNativeShare &&
    isTouchDevice() &&
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    typeof File !== 'undefined'
  ) {
    const shareFile = new File([blob], String(filename || 'download.bin'), {
      type: blob.type || contentType,
      lastModified: Date.now(),
    });

    if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [shareFile] })) {
      await navigator.share({
        files: [shareFile],
        title: 'Fluffball',
      });
      return { method: 'native_share' };
    }
  }

  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = String(filename || 'download.bin');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
  return { method: 'download' };
};

const extractEmails = (text) => {
    const emailRegex = /[\w.-]+@[\w.-]+\.\w+/g;
    return text.match(emailRegex) || [];
};

const searchEmails = async (extractedEmails, hasBeenReviewed) => {
    const petPhotosCollection = collection(db, 'petPhotos');
    const queries = extractedEmails.map(email => query(petPhotosCollection, 
    	where("email", "==", email),
    	where("has_been_reviewed", "==", hasBeenReviewed)
    ));
    
    try {
        const results = await Promise.all(queries.map(q => getDocs(q)));
        const documents = results.flatMap(result => 
            result.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        );
        return documents;
    } catch (error) {
        console.error('Error fetching documents:', error);
        throw error; // or handle it as needed
    }
};

const searchDocumentsByReviewer = async (reviewer, hasBeenReviewed) => {
    const petPhotosCollection = collection(db, 'petPhotos');
    const assignmentQuery = query(petPhotosCollection, 
    	where("reviewer", "==", reviewer),
    	where("has_been_reviewed", "==", hasBeenReviewed)
    );

    try {
        const querySnapshot = await getDocs(assignmentQuery);
        const documents = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        return documents;
    } catch (error) {
        console.error('Error fetching documents:', error);
        throw error; // or handle it as needed
    }
};

const updateImageQuality = async (docId, newImageQuality, outputType) => {
    try {
        if (outputType === "special")
          await updateDoc(doc(db, 'petPhotos', docId), { special_image_quality: newImageQuality, has_been_reviewed: true, });
        else
          await updateDoc(doc(db, 'petPhotos', docId), { image_quality: newImageQuality, has_been_reviewed: true, });
    } catch (error) {
        console.error('Error updating document:', error);
        throw error; // Or handle it as needed
    }
};

const addEmailsToShare = async (docId, emailsToShare, firstName) => {
    console.log(`first name: ${firstName}`)
    try {
      await updateDoc(doc(db, 'petPhotos', docId), { emails_to_share: emailsToShare, user_first_name: firstName });
      // await updateDoc(doc(db, 'petPhotos', docId), { user_first_name: firstName });
    } catch (error) {
        console.error('Error updating document:', error);
        throw error; // Or handle it as needed
    }
};

const updateChatImagesInFirestore = async (docId, imageUrls) => {
  const firestore = getFirestore();
  const docRef = doc(firestore, "petPhotos", docId); // Replace 'collectionName' with your actual collection name

  await updateDoc(docRef, {
    chat_images: imageUrls,
    timestamp: serverTimestamp()
  });
};


const uploadImageToFirebase = async (imageFile) => {
  const authUser = await requireSignedInUser();
  const storageUserId = authUser.uid;
  const photoId = uuidv4();
  const fileExtension = imageFile.name ? imageFile.name.split('.').pop().toLowerCase() : 'png'; // Defaulting to 'jpg' if extension is missing

  // Set the metadata to make the file publicly accessible
  const metadata = {
    contentType: imageFile.type,
    cacheControl: 'public, max-age=31536000',
  };

  const photoRef = ref(storage, `chat_images/${storageUserId}/${photoId}.${fileExtension}`);
  const uploadTask = uploadBytesResumable(photoRef, imageFile.file);

  // Monitor the upload progress
  return await new Promise((resolve, reject) => {
    uploadTask.on('state_changed', 
      (snapshot) => {
        // Update progress
      },
      (error) => {
        console.error('Upload failed: ', error);
        reject(error);
      }, 
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        resolve(downloadURL);
      }
    );
  });
};

const uploadVideoToFirebase = async (videoFile) => {
  const authUser = await requireSignedInUser();
  const storageUserId = authUser.uid;
  const videoId = uuidv4();
  const fileExtension = videoFile.name ? videoFile.name.split('.').pop().toLowerCase() : 'mp4'; // Defaulting to 'jpg' if extension is missing

  // Set the metadata to make the file publicly accessible
  const metadata = {
    contentType: videoFile.type,
    cacheControl: 'public, max-age=31536000',
  };

  const photoRef = ref(storage, `user_submitted_videos/${storageUserId}/${videoId}.${fileExtension}`);
  const uploadTask = uploadBytesResumable(photoRef, videoFile.file);

  // Monitor the upload progress
  return await new Promise((resolve, reject) => {
    uploadTask.on('state_changed', 
      (snapshot) => {
        // Update progress
      },
      (error) => {
        console.error('Upload failed: ', error);
        reject(error);
      }, 
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        resolve(downloadURL);
      }
    );
  });
};

const updateVideoInFirestore = async (docId, videoUrl) => {
  const firestore = getFirestore();
  const docRef = doc(firestore, "petPhotos", docId); // Replace 'collectionName' with your actual collection name

  await updateDoc(docRef, {
    pet_video: videoUrl,
    timestamp: serverTimestamp()
  });
};

async function getDocumentWithId(docId) {
    try {
        const docRef = doc(db, 'petPhotos', docId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            console.log("No such document!");
            return null;
        }
    } catch (error) {
        console.error("Error fetching document:", error);
        throw error;
    }
}

const updateSharedStatus = async (docId) => {
    try {
      await updateDoc(doc(db, 'petPhotos', docId), { shared_screenshots_verified: true });
      // await updateDoc(doc(db, 'petPhotos', docId), { user_first_name: firstName });
    } catch (error) {
        console.error('Error updating document:', error);
        throw error; // Or handle it as needed
    }
};



// const storeFile = (file, fileName) => {
//   return new Promise((resolve, reject) => {
//     const filePath = `audios/${fileName || uuidv4()}`;
//     const storageRef = ref(storage, filePath);

//     const uploadTask = uploadBytesResumable(storageRef, file);

//     uploadTask.on('state_changed', 
//       (snapshot) => {
//         // Observe state change events such as progress, pause, and resume
//       }, 
//       (error) => {
//         // Handle unsuccessful uploads
//         console.error('Upload failed', error);
//         reject(error);
//       }, 
//       async () => {
//         // Do something once upload is complete
//         try {
//           const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
//           console.log('File available at', downloadURL);
//           resolve(downloadURL);
//         } catch (error) {
//           console.error('Failed to get download URL', error);
//           reject(error);
//         }
//       }
//     );
//   });
// };

export {
  app,
  storage,
  db,
  BACKEND_BASE_URL,
  saveEmailToFirebase,
  logCheckoutClick,
  createDraftPetPhotoDoc,
  onUploadPhotos,
  persistPreparedReferencePhoto,
  prepareReferencePhotoUrl,
  validateSelectedReferencePhotoFile,
  updateEmailAndPetTypeInFirebase,
  updatePetTypeInFirebase,
  updatePetQuestionnaireInFirebase,
  subscribeToPetPhotoDocument,
  startGenerationJob,
  createSubscriptionCheckoutSession,
  createWinbackCheckoutSession,
  fetchWinbackOfferDetails,
  fetchWinbackSupportDetails,
  createBillingPortalSession,
  openDeferredExternalUrl,
  createShareLink,
  fetchShareById,
  startDailyVideoGeneration,
  debugResultAccess,
  downloadMediaViaBackend,
  getAnalyticsEventContext,
  getAnalyticsUserId,
  logAnalyticsEvent,
  getAuthenticatedJsonHeaders,
  searchEmails,
  extractEmails,
  updateImageQuality,
  searchDocumentsByReviewer,
  addEmailsToShare,
  uploadImageToFirebase,
  updateChatImagesInFirestore,
  getDocumentWithId,
  updateSharedStatus,
  uploadVideoToFirebase,
  updateVideoInFirestore,
  signInWithApple,
  signInWithGoogle,
  consumePendingAuthRedirectResult,
  completeFlowSignInWithApple,
  completeFlowSignInWithGoogle,
  sendEmailSignInLinkForFlow,
  consumePendingEmailLinkSignIn,
  completePendingEmailLinkSignIn,
  completeFlowEmailPasswordAuth,
  shouldPreferMagicLinkEmailAuth,
  signOutCurrentUser,
  onAuthUserChanged,
  getCurrentUser,
  hasResolvedInitialAuthState,
  waitForInitialAuthState,
  getUiBootstrapSnapshot,
  hasAuthoritativeHomeProfileData,
  canAccessProfileFromProfile,
  isSubscriptionActiveFromProfile,
  getHomeStateFromProfile,
  getHomeActionDocIdFromProfile,
  buildHomepageSnapshotFromProfile,
  cacheUserProfileSnapshot,
  cacheHomepageSnapshot,
  cacheHomepageSnapshotFromProfile,
  cacheProfileDocumentsSnapshot,
  getHomeStateDebugEnabled,
  getHomeStateDebugHistory,
  pushHomeStateDebugEvent,
  ensureUserProfileDocument,
  getUserProfile,
  subscribeToUserProfile
}
